/**
 * Scroll-triggered Reveal Animations
 * ==================================
 * Two reveal modes — pick whichever fits the markup:
 *
 * 1. Element-based:
 *    <div class="scroll-trigger animate--slide-in">...</div>
 *    The element gets `is-revealed` once it scrolls into view, which kicks off
 *    the animation defined in base.css.
 *
 * 2. Section-based (cascading children inside a wrapper):
 *    <section class="section-scroll-trigger">
 *      <div class="scroll-trigger animate--slide-in">...</div>
 *    </section>
 *    The wrapper gets `section-active` when scrolled into view, which animates
 *    every `.scroll-trigger` child via the cascade.
 *
 * Both honour `prefers-reduced-motion` via CSS.
 */

const SECTION_TRIGGER_CLASS = "section-scroll-trigger";
const ELEMENT_TRIGGER_CLASS = "scroll-trigger";
const ACTIVE_SECTION_CLASS = "section-active";
const REVEALED_CLASS = "is-revealed";

function onSectionIntersection(entries, observer) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add(ACTIVE_SECTION_CLASS);
      observer.unobserve(entry.target);
    }
  });
}

function onElementIntersection(entries, observer) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add(REVEALED_CLASS);
      observer.unobserve(entry.target);
    }
  });
}

function initializeScrollAnimations(rootEl = document, isDesignMode = false) {
  // Section wrappers
  const sections = Array.from(
    rootEl.getElementsByClassName(SECTION_TRIGGER_CLASS)
  );

  if (isDesignMode) {
    sections.forEach((section) =>
      section.classList.add("scroll-trigger--design-mode")
    );
    rootEl
      .querySelectorAll("." + ELEMENT_TRIGGER_CLASS)
      .forEach((element) => {
        element.classList.add("scroll-trigger--design-mode");
        element.classList.add(REVEALED_CLASS);
      });
    return;
  }

  if (sections.length > 0) {
    const sectionObserver = new IntersectionObserver(onSectionIntersection, {
      rootMargin: "0px 0px -20% 0px",
      threshold: 0.1,
    });
    sections.forEach((section) => sectionObserver.observe(section));
  }

  // Individual scroll-triggers (excluding ones already governed by an active
  // section wrapper, since those reveal via the cascade rule).
  const elements = Array.from(
    rootEl.getElementsByClassName(ELEMENT_TRIGGER_CLASS)
  ).filter((el) => !el.closest("." + SECTION_TRIGGER_CLASS));

  if (elements.length === 0) return;

  const elementObserver = new IntersectionObserver(onElementIntersection, {
    rootMargin: "0px 0px -50px 0px",
  });
  elements.forEach((element) => elementObserver.observe(element));
}

window.addEventListener("DOMContentLoaded", () => {
  initializeScrollAnimations();
});

if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) =>
    initializeScrollAnimations(event.target, true)
  );
  document.addEventListener("shopify:section:reorder", () =>
    initializeScrollAnimations(document, true)
  );
}
